import React from 'react';
import { Link } from 'react-router-dom';

export const meta = {
  slug: 'section-32-checklist',
  title: 'Section 32 checklist: 10 things to check before you sign',
  navTitle: 'Section 32 checklist',
  description:
    'A practical checklist for reading a Victorian Section 32 vendor statement — the ten things to verify, the red flags for each, and where in the document to find them.',
  published: '2026-07-14',
  readingTime: '8 min read',
  teaser:
    'Ten specific things to verify in a vendor statement, the red flags for each, and where in the document they hide.',
};

export default function Section32Checklist() {
  return (
    <>
      <p className="guide-lede">
        A Section 32 can run to 150 pages, and the parts that matter are rarely at the
        front. This is the checklist we'd give a friend buying their first Victorian
        property: ten things to verify, what a red flag looks like for each, and where in
        the bundle to find it.
      </p>

      <h2>1. The title matches the property — and the seller</h2>
      <p>
        On the Register Search Statement (usually the first attachment), check the
        registered proprietor matches the vendor named in the contract, and the lot and
        plan number (e.g. <em>Lot 12 on PS 543210</em>) matches the address you inspected.
        Check the search date too — a title search from eight months ago can miss recent
        caveats or mortgages.
      </p>
      <p className="guide-flag">
        Red flags: vendor name doesn't match the title; a caveat lodged by someone other
        than a lender; a stale search date.
      </p>

      <h2>2. Easements — where they are and what they stop you doing</h2>
      <p>
        Look for "SUBJECT TO" entries on the title and hatched strips labelled E-1, E-2 on
        the plan of subdivision. A two-metre sewerage easement along the back fence is
        routine; the same easement running under the middle of the yard can rule out a
        pool or extension. Water authority certificates near the back of the bundle often
        disclose easements the title summary doesn't spell out.
      </p>
      <p className="guide-flag">
        Red flags: an easement through the building envelope; structures already built
        over an easement; a carriageway easement giving someone else access across the lot.
        See our <Link to="/guides/easements-explained">full easements guide</Link>.
      </p>

      <h2>3. Restrictive covenants</h2>
      <p>
        Covenants are promises attached to the land — commonly "single dwelling only", no
        subdivision, or materials restrictions from the original estate. They bind you
        regardless of what today's zoning allows, and removing one is slow and uncertain.
      </p>
      <p className="guide-flag">
        Red flags: a single-dwelling covenant on a block you're buying for its development
        potential; building-material covenants that don't match your renovation plans.
      </p>

      <h2>4. Zoning and overlays</h2>
      <p>
        The planning certificate states the zone (General Residential, Neighbourhood
        Residential, etc.) and overlays. Overlays are where surprises live: a Heritage
        Overlay can restrict external changes, a Bushfire Management Overlay adds
        construction cost, and flood-related overlays (LSIO, SBO) affect both building
        rules and insurance premiums.
      </p>
      <p className="guide-flag">
        Red flags: any overlay you haven't priced in; a zone that doesn't allow what you
        plan to do with the property.
      </p>

      <h2>5. Outgoings — the real annual cost of owning</h2>
      <p>
        Add up council rates, water service charges, owners corporation levies and (if
        disclosed) land tax. For apartments, the OC levies are frequently the largest
        line. This total is what the property costs you every year before a single repair.
      </p>
      <p className="guide-flag">
        Red flags: OC fees that look too low for a building with lifts and a gym (deferred
        maintenance); amounts left blank or "to be advised".
      </p>

      <h2>6. The owners corporation certificate (apartments and townhouses)</h2>
      <p>
        Read the OC certificate for: current levies, the maintenance fund balance, special
        levies struck or proposed, building insurance (sum insured and expiry), legal
        proceedings, and planned major works. A healthy fund and current insurance are
        the baseline; anything else is a question for the agent.
      </p>
      <p className="guide-flag">
        Red flags: a special levy that lands after settlement; litigation involving the
        OC (especially cladding-related); a maintenance fund that couldn't cover one lift
        replacement.
      </p>

      <h2>7. Building permits and owner-builder works</h2>
      <p>
        The statement must list building permits issued in the last seven years. Compare
        the list with what you saw at the inspection: a new deck, garage conversion or
        second bathroom with no matching permit is your problem after settlement. If the
        vendor was an owner-builder, check the required warranty insurance is attached for
        works over the threshold.
      </p>
      <p className="guide-flag">
        Red flags: obvious recent works with no permit listed; owner-builder works without
        insurance; a permit with no final inspection/occupancy approval.
      </p>

      <h2>8. Notices, orders and proposals</h2>
      <p>
        This section discloses notices from authorities — road widening, compulsory
        acquisition, building orders, land contamination notices. It's usually empty.
        When it isn't, treat whatever appears as a headline issue.
      </p>
      <p className="guide-flag">
        Red flags: anything at all. Every entry here deserves a conversation with your
        conveyancer before you sign.
      </p>

      <h2>9. Services not connected</h2>
      <p>
        The statement lists which of electricity, gas, water, sewerage and phone are{' '}
        <em>not</em> connected. On established suburban lots this is rarely an issue; on
        edge-of-town and rural properties, connection costs can run to tens of thousands
        — septic, tank water and off-grid power change the ownership economics entirely.
      </p>
      <p className="guide-flag">
        Red flags: no sewerage connection where you assumed town sewer; no water
        connection on a lot you plan to build on.
      </p>

      <h2>10. What the Section 32 can't tell you</h2>
      <p>
        Finally, remember the boundaries of the document itself (our{' '}
        <Link to="/guides/what-is-a-section-32">Section 32 explainer</Link> covers this in
        detail): it says nothing about building condition, pests, or whether older
        structures were ever approved. Budget for a building and pest inspection, and read
        the contract's special conditions separately — that's where deposit terms, penalty
        interest and sunset clauses live, not in the Section 32.
      </p>

      <h2>Working through it</h2>
      <p>
        The pattern across all ten: the front pages of a vendor statement are forms; the
        evidence is in the attachments — title search, plan of subdivision, planning
        certificate, water authority search, OC certificate, rate notices. Read the
        attachments first, then check the forms agree with them.
      </p>
    </>
  );
}
